import { useEffect, useMemo, useState } from "react";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  isoToLocalDate,
  isoToMxDisplay,
  localDateToIso,
  parseMxDateToIso,
  satMinDownloadDateIso,
  todayIsoMx,
} from "@/lib/dateMx";

/**
 * Campo de fecha con calendario (es-MX) y formato visible DD/MM/AAAA.
 * ``value`` / ``onChange`` usan ISO ``YYYY-MM-DD`` para la API / SAT.
 */
export default function DateFieldMx({
  value,
  onChange,
  disabled,
  className = "",
  placeholder = "dd/mm/aaaa",
  showCalendar = true,
  minIso,
  maxIso,
  ...rest
}) {
  const [text, setText] = useState(() => isoToMxDisplay(value));
  const [open, setOpen] = useState(false);

  const minDateIso = minIso ?? satMinDownloadDateIso();
  const maxDateIso = maxIso ?? todayIsoMx();

  const fromYear = useMemo(() => {
    const y = parseInt(String(minDateIso).slice(0, 4), 10);
    return Number.isFinite(y) ? y : 2000;
  }, [minDateIso]);

  const toYear = useMemo(() => {
    const y = parseInt(String(maxDateIso).slice(0, 4), 10);
    return Number.isFinite(y) ? y : fromYear + 5;
  }, [maxDateIso, fromYear]);

  const fromDate = useMemo(() => isoToLocalDate(minDateIso), [minDateIso]);
  const toDate = useMemo(() => isoToLocalDate(maxDateIso), [maxDateIso]);

  const [month, setMonth] = useState(() => isoToLocalDate(value) || isoToLocalDate(maxDateIso));

  useEffect(() => {
    setText(isoToMxDisplay(value));
  }, [value]);

  const selectedDate = useMemo(() => isoToLocalDate(value), [value]);

  useEffect(() => {
    if (selectedDate) {
      setMonth(selectedDate);
    }
  }, [selectedDate]);

  const isIsoOutOfRange = (iso) => {
    if (!iso) return false;
    if (iso < minDateIso) return true;
    if (iso > maxDateIso) return true;
    return false;
  };

  const commit = (raw) => {
    const trimmed = (raw || "").trim();
    if (!trimmed) {
      setText("");
      onChange?.("");
      return;
    }
    const iso = parseMxDateToIso(trimmed);
    if (iso && !isIsoOutOfRange(iso)) {
      setText(isoToMxDisplay(iso));
      onChange?.(iso);
    } else {
      setText(isoToMxDisplay(value));
    }
  };

  const pickCalendarDate = (date) => {
    if (!date) return;
    const iso = localDateToIso(date);
    if (!iso || isIsoOutOfRange(iso)) return;
    setText(isoToMxDisplay(iso));
    onChange?.(iso);
    setOpen(false);
  };

  const calendarDisabled = (date) => {
    const iso = localDateToIso(date);
    return isIsoOutOfRange(iso);
  };

  const field = (
    <Input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      lang="es-MX"
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit(text);
      }}
      disabled={disabled}
      className={cn(showCalendar && "pr-9", className)}
      aria-label={placeholder}
      {...rest}
    />
  );

  if (!showCalendar) {
    return field;
  }

  return (
    <div className="relative">
      {field}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="absolute right-0 top-0 h-9 w-9 text-slate-500 hover:text-[#0F2D5C]"
            aria-label="Abrir calendario"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={es}
            month={month}
            onMonthChange={setMonth}
            selected={selectedDate}
            onSelect={pickCalendarDate}
            disabled={calendarDisabled}
            captionLayout="dropdown"
            fromYear={fromYear}
            toYear={toYear}
            fromDate={fromDate}
            toDate={toDate}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
